import React, { useState, useEffect } from 'react';
import { useAuth } from '../../Auth/AuthContext';
import { updatePassword, updateProfile } from 'firebase/auth';
import { auth } from '../../../config/firebase';
import { FirebaseService } from '../../../services/FirebaseService';
import { ProfileImageEditor } from './ProfileImageEditor';
import { DeleteAccountDialog } from './DeleteAccountDialog';
import { profileImageEmitter, userProfileEmitter } from '../../../hooks/useProfileImage';
import './Settings.css';

export const Settings: React.FC = () => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    displayName: user?.displayName || '',
    email: user?.email || '',
    company: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [activeSection, setActiveSection] = useState('profile');
  const [profileImageUrl, setProfileImageUrl] = useState<string>('');
  const [imageKey, setImageKey] = useState(Date.now()); // Force re-render of image
  const [userCompanyId, setUserCompanyId] = useState<string | null>(null);
  const [companyCode, setCompanyCode] = useState<string>('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Generate user initials
  const userInitials = user?.displayName 
    ? user.displayName.split(' ').map(n => n[0]).join('').toUpperCase()
    : user?.email?.[0]?.toUpperCase() || 'U';

  // Fetch user profile on component mount
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const profile = await FirebaseService.fetchUserProfile();
        if (profile?.profileImageUrl) {
          setProfileImageUrl(profile.profileImageUrl);
        }

        // Check if user has a company
        if (profile?.companyId) {
          setUserCompanyId(profile.companyId);
          try {
            const companyDetails = await FirebaseService.getCompanyDetails(profile.companyId);
            if (companyDetails?.code) {
              setCompanyCode(companyDetails.code);
              setFormData(prev => ({
                ...prev,
                company: companyDetails.code
              }));
            }
          } catch (error) {
            console.error('❌ Settings - Error fetching company details:', error);
          }
        } else {
          setUserCompanyId(null);
          setCompanyCode('');
        }
      } catch (error) {
        console.error('❌ Settings - Error fetching user profile:', error);
      }
    };

    if (user) {
      fetchUserProfile();
    }
  }, [user]);

  const handleImageUpdate = (newImageUrl: string) => {
    //console.log('� Settings.handleImageUpdate called');
    //console.log('📄 Previous profileImageUrl:', profileImageUrl);
    //console.log('📄 New image URL received:', newImageUrl);
    
    setProfileImageUrl(newImageUrl);
    const newKey = Date.now();
    setImageKey(newKey); // Force component re-render
    
    // Emit the update to notify other components (like Sidebar)
    profileImageEmitter.emit(newImageUrl);
    
    //console.log('🔑 Set new image key for re-render:', newKey);
    //console.log('✅ Updated profileImageUrl state to:', newImageUrl);
    
    setMessage({ type: 'success', text: 'Profile picture updated successfully!' });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage(null);

    try {
      if (auth.currentUser) {
        // Update display name in Firebase Auth
        await updateProfile(auth.currentUser, {
          displayName: formData.displayName
        });

        // Emit the display name update to notify other components (like Sidebar)
        userProfileEmitter.emit({ displayName: formData.displayName });

        // You can also save additional profile data to Firestore if needed
        // await FirebaseService.updateUserProfile(auth.currentUser.uid, {
        //   company: formData.company
        // });

        setMessage({ type: 'success', text: 'Profile updated successfully!' });
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setMessage({ type: 'error', text: 'Failed to update profile. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage(null);

    if (formData.newPassword !== formData.confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match.' });
      setIsLoading(false);
      return;
    }

    if (formData.newPassword.length < 6) {
      setMessage({ type: 'error', text: 'Password must be at least 6 characters long.' });
      setIsLoading(false);
      return;
    }

    try {
      if (auth.currentUser) {
        await updatePassword(auth.currentUser, formData.newPassword);
        setMessage({ type: 'success', text: 'Password updated successfully!' });
        setFormData(prev => ({
          ...prev,
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        }));
      }
    } catch (error: any) {
      console.error('Error updating password:', error);
      let errorMessage = 'Failed to update password. Please try again.';
      
      if (error.code === 'auth/requires-recent-login') {
        errorMessage = 'Please sign out and sign back in before changing your password.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password is too weak. Please choose a stronger password.';
      }
      
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="settings">
      <div className="settings-header">
        <h1 className="settings-title">Settings</h1>
        <p className="settings-subtitle">Manage your account and preferences</p>
      </div>

      <div className="settings-content">
        <div className="settings-nav">
          <button
            className={`settings-nav-item ${activeSection === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveSection('profile')}
          >
            Profile
          </button>
          <button
            className={`settings-nav-item ${activeSection === 'security' ? 'active' : ''}`}
            onClick={() => setActiveSection('security')}
          >
            Security
          </button>
        </div>

        <div className="settings-main">
          {message && (
            <div className={`settings-message ${message.type}`}>
              {message.text}
            </div>
          )}

          {activeSection === 'profile' && (
            <div className="settings-section">
              <h2 className="section-title">Profile Information</h2>
              
              <div className="profile-image-section">
                <h3 className="subsection-title">Profile Picture</h3>
                <ProfileImageEditor 
                  key={imageKey}
                  currentImageUrl={profileImageUrl}
                  initials={userInitials}
                  size={120}
                  onImageUpdate={handleImageUpdate}
                />
              </div>

              <form onSubmit={handleProfileUpdate} className="settings-form">
                <div className="form-group">
                  <label htmlFor="displayName">Full Name</label>
                  <input
                    type="text"
                    id="displayName"
                    name="displayName"
                    value={formData.displayName}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter your full name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    className="form-input"
                    disabled
                    title="Email cannot be changed from here"
                  />
                  <small className="form-help">Email address cannot be changed</small>
                </div>

                {/* Only show company section if user is in a company */}
                {userCompanyId && (
                  <div className="form-group">
                    <label htmlFor="company">Company Code</label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      className="form-input"
                      disabled
                      title="Company code cannot be changed from here"
                    />
                    <small className="form-help">Company code is read-only</small>
                  </div>
                )}

                <div className="form-actions">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Updating...' : 'Update Profile'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {activeSection === 'security' && (
            <div className="settings-section">
              <h2 className="section-title">Change Password</h2>
              <form onSubmit={handlePasswordChange} className="settings-form">
                <div className="form-group">
                  <label htmlFor="newPassword">New Password</label>
                  <input
                    type="password"
                    id="newPassword"
                    name="newPassword"
                    value={formData.newPassword}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter new password"
                    minLength={6}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm New Password</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Confirm new password"
                    minLength={6}
                    required
                  />
                </div>

                <div className="form-actions">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isLoading || !formData.newPassword || !formData.confirmPassword}
                  >
                    {isLoading ? 'Updating...' : 'Change Password'}
                  </button>
                </div>
              </form>

              <div className="settings-section" style={{ marginTop: '2rem', borderTop: '1px solid #e0e0e0', paddingTop: '2rem' }}>
                <h2 className="section-title" style={{ color: '#d32f2f' }}>Danger Zone</h2>
                <p style={{ color: '#666', marginBottom: '1rem' }}>
                  Once you delete your account, there is no going back. Please be certain.
                </p>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => setShowDeleteDialog(true)}
                  style={{
                    backgroundColor: '#d32f2f',
                    color: 'white',
                    border: '2px solid #d32f2f',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '1rem'
                  }}
                >
                  Delete Account
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <DeleteAccountDialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onSuccess={() => {
          // User successfully deleted account and signed out
          // Auth context will handle redirect
        }}
      />
    </div>
  );
};
